import { useState, useEffect } from "react";
import { getApplicationDetail, approveApplication, rejectApplication } from "../services/applications.js";

const STATUS_LABELS = {
  pending_details: { label: "Pending Details", color: "#94a3b8" },
  pending_verification: { label: "Pending Verification", color: "#f59e0b" },
  pending_approval: { label: "Awaiting Review", color: "#3b82f6" },
  approved: { label: "Approved", color: "#10b981" },
  rejected: { label: "Rejected", color: "#ef4444" },
  invited: { label: "Invited", color: "#8b5cf6" },
};

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#64748b", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: "#e2e8f0", lineHeight: 1.6 }}>{value}</div>
    </div>
  );
}

function DocLink({ label, url }) {
  if (!url) return <div style={{ color: "#475569", fontSize: 13 }}>No {label.toLowerCase()} uploaded</div>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#1e3a5f", borderRadius: 8, color: "#60a5fa", fontSize: 13, textDecoration: "none", fontWeight: 600 }}>
      📄 View {label}
    </a>
  );
}

export function ApplicationDetail({ applicationId, onBack, onStatusChange }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!applicationId) return;
    setLoading(true);
    getApplicationDetail(applicationId)
      .then((data) => setApp(data.application))
      .catch((err) => setError(err.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [applicationId]);

  const handleApprove = async () => {
    if (!confirm("Approve this application? This will create the persona and notify the applicant.")) return;
    setActionLoading(true);
    try {
      const { application } = await approveApplication(applicationId);
      setApp(application);
      onStatusChange?.();
    } catch (err) {
      alert(err.message || "Approval failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    try {
      const { application } = await rejectApplication(applicationId, rejectReason);
      setApp(application);
      setShowReject(false);
      setRejectReason("");
      onStatusChange?.();
    } catch (err) {
      alert(err.message || "Rejection failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div style={{ padding: "24px 28px", color: "#64748b" }}>Loading...</div>;
  if (error) return <div style={{ padding: "24px 28px", color: "#ef4444" }}>{error}</div>;
  if (!app) return null;

  const s = STATUS_LABELS[app.status] || { label: app.status, color: "#94a3b8" };
  const canApprove = app.status === "pending_approval";

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900 }}>
      {/* Header */}
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13, marginBottom: 20, padding: 0, fontFamily: "inherit" }}>
        ← Back to Applications
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#1e3a5f", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#60a5fa" }}>
          {(app.officialName || "?").charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", margin: 0 }}>
            {app.officialName || "Unnamed Application"}
          </h1>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
            {app.electoralPosition || "No position"} · Applied {new Date(app.createdAt).toLocaleDateString()}
          </div>
        </div>
        <div style={{ padding: "6px 14px", borderRadius: 20, background: `${s.color}20`, color: s.color, fontSize: 12, fontWeight: 700 }}>
          {s.label}
        </div>
      </div>

      {/* Details card */}
      <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
          Application Details
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          <Field label="Email" value={app.email} />
          <Field label="Phone" value={app.phone} />
          <Field label="Electoral Position" value={app.electoralPosition} />
          {/* v1.27.0: Party field removed from the admin application review
              view. Historical `app.party` values remain in the DB (product
              decision to keep the column on persona_owner_applications), but
              they're no longer surfaced to admins reviewing applications —
              consistent with removing party from the voter-facing product. */}
          <Field label="Country" value={app.geoCountry} />
          <Field label="State" value={app.geoState} />
          <Field label="LGA" value={app.geoLga} />
        </div>
        <Field label="Bio" value={app.bio} />
      </div>

      {/* Documents card */}
      <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
          Verification Documents
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <DocLink label="Government ID" url={app.idDocumentSignedUrl} />
          <DocLink label="Authorisation Letter" url={app.authLetterSignedUrl} />
        </div>
      </div>

      {/* Rejection reason (if rejected) */}
      {app.status === "rejected" && app.rejectionReason && (
        <div style={{ background: "#450a0a", border: "1px solid #991b1b", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
            Rejection Reason
          </div>
          <div style={{ fontSize: 14, color: "#fca5a5", lineHeight: 1.6 }}>{app.rejectionReason}</div>
        </div>
      )}

      {/* Action buttons */}
      {canApprove && (
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button
            onClick={handleApprove}
            disabled={actionLoading}
            style={{ flex: 1, padding: "14px", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none", borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: actionLoading ? 0.7 : 1 }}
          >
            {actionLoading ? "Processing..." : "✅ Approve Application"}
          </button>
          <button
            onClick={() => setShowReject(true)}
            disabled={actionLoading}
            style={{ flex: 1, padding: "14px", background: "transparent", color: "#ef4444", border: "1px solid #ef444440", borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
          >
            ❌ Reject
          </button>
        </div>
      )}

      {/* Reject modal */}
      {showReject && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 14, padding: 28, maxWidth: 480, width: "100%" }}>
            <h3 style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, margin: "0 0 6px", fontFamily: "Georgia, serif" }}>Reject Application</h3>
            <p style={{ color: "#64748b", fontSize: 13, marginBottom: 16 }}>
              Provide a reason. The applicant will be notified and can re-apply with corrections.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Authorisation letter is not on official letterhead..."
              rows={4}
              style={{ width: "100%", padding: "12px 14px", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 10, color: "#e2e8f0", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading}
                style={{ flex: 1, padding: "12px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: rejectReason.trim() ? "pointer" : "default", fontFamily: "inherit", opacity: !rejectReason.trim() || actionLoading ? 0.5 : 1 }}
              >
                {actionLoading ? "Rejecting..." : "Reject Application"}
              </button>
              <button
                onClick={() => { setShowReject(false); setRejectReason(""); }}
                style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #1e2d45", color: "#94a3b8", borderRadius: 10, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationDetail;
